import React, { useState } from 'react';
import { apiClient } from '../../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT } from '../legacy-ball/table-management/designTokens';

interface BugReportModalProps {
  onClose: () => void;
}

export const BugReportModal: React.FC<BugReportModalProps> = ({ onClose }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      await apiClient('/error-reports', {
        method: 'POST',
        body: {
          screenSlug: 'mobile-attendee',
          screenAlias: 'Mobile App',
          currentUrl: window.location.href,
          browserName: navigator.userAgent.includes('Safari') ? 'Safari' : 'Mobile Browser',
          browserVersion: 'N/A',
          deviceOs: /iPhone|iPad/.test(navigator.userAgent) ? 'iOS' : 'Android',
          description: `${title.trim()}${description.trim() ? '\n\n' + description.trim() : ''}`,
          severity: 'Medium',
        },
      });
      setSubmitted(true);
      setTimeout(onClose, 1500);
    } catch {
      setSubmitted(true);
      setTimeout(onClose, 1500);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {submitted ? (
          <div style={styles.successContainer}>
            <span style={{ fontSize: '2rem' }}>✅</span>
            <p style={styles.successText}>Thanks for the feedback!</p>
          </div>
        ) : (
          <>
            <div style={styles.header}>
              <h3 style={styles.title}>🐛 Report a Bug</h3>
              <button style={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
            </div>

            <div style={styles.body}>
              <label style={styles.label}>What went wrong? *</label>
              <input
                style={styles.input}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Button doesn't respond on Events page"
                autoFocus
              />

              <label style={styles.label}>Details (optional)</label>
              <textarea
                style={styles.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Steps to reproduce, what you expected, what happened instead..."
                rows={4}
              />
            </div>

            <div style={styles.footer}>
              <button
                style={styles.submitBtn}
                onClick={handleSubmit}
                disabled={submitting || !title.trim()}
              >
                {submitting ? 'Sending...' : 'Submit Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '360px',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px 12px',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: 700,
    color: TEXT_DARK,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.2rem',
    color: TEXT_MUTED,
    cursor: 'pointer',
    padding: '4px',
  },
  body: {
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  label: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    marginTop: '4px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.9rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.9rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    outline: 'none',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  },
  footer: {
    padding: '12px 20px 20px',
  },
  submitBtn: {
    width: '100%',
    padding: '12px',
    fontSize: '0.95rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    gap: '12px',
  },
  successText: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
};
